import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Dispatch, SetStateAction, memo, useCallback } from "react";
import { Updater } from "use-immer";

import "./EnvironmentRow.scss";
import Container from "../../../../../component/container/Container";
import {
  GenericGraphDataEntry,
  OverallRecycledWasteEntry,
} from "../../../../../service/utils/interfaces";
import RecycledRecoveredLegend from "../../../../../component/recycled-recovered-legend/RecycledRecoveredLegend";
import {
  changeFilterLeaderboardAction,
  leaderboardReverseSorting,
} from "../../../../../service/utils/functions";
import { SortOrder } from "../../../../../service/utils/constants";
import { WasteTotalVerticalEntry } from "../../../../../service/utils/interfaces";
import LeaderboardGraph from "../../../../../component/leaderboard-graph/LeaderboardGraph";
import WTPTooltip from "../../../../../component/wtp-tooltip/WTPTooltip";
import LevelLegend from "../../../../../component/level-legend/LevelLegend";

interface EnvironmentRowProps {
  isTippingUser: boolean;
  overallRecycledWastes: OverallRecycledWasteEntry[];
  overallRecycledWastesColors: any[];
  emissionsGeneratedDetails: GenericGraphDataEntry[];
  filterLeaderboardValue: number;
  sortingLeaderboardValue: SortOrder;
  leaderboardRecycling: {top: WasteTotalVerticalEntry[], bottom: WasteTotalVerticalEntry[]};
  leaderboardEmissions: {top: WasteTotalVerticalEntry[], bottom: WasteTotalVerticalEntry[]};
  leaderboardWaste: {top: WasteTotalVerticalEntry[], bottom: WasteTotalVerticalEntry[]};
  levelDataTable: any[];
  feeDataTable: any[];
  tonnageDataTable: any[];

  setSortingLeaderboardValue: Dispatch<SetStateAction<SortOrder>>;
  setFilterLeaderboardValue: Dispatch<SetStateAction<number>>;

  darkMode: boolean;
  width: number;
}

const EnvironmentRow = memo((props: EnvironmentRowProps) => {
  const handleRecyclingRateClick = useCallback(() => {
    if (props.filterLeaderboardValue === 1) {
        props.setSortingLeaderboardValue(
            props.sortingLeaderboardValue === SortOrder.Descending
            ? SortOrder.Ascending
            : SortOrder.Descending
        );
    } else {
        props.setFilterLeaderboardValue(1);
        props.setSortingLeaderboardValue(SortOrder.Descending);
    }
  }, [
    props.filterLeaderboardValue,
    props.sortingLeaderboardValue,
    props.setSortingLeaderboardValue,
    props.setFilterLeaderboardValue,
  ]);

  const handleEmissionsRateClick = useCallback(() => {
    if (props.filterLeaderboardValue === 2) {
        props.setSortingLeaderboardValue(
            props.sortingLeaderboardValue === SortOrder.Ascending
            ? SortOrder.Descending
            : SortOrder.Ascending
        );
    } else {
        props.setFilterLeaderboardValue(2);
        props.setSortingLeaderboardValue(SortOrder.Ascending);
    }
  }, [
    props.filterLeaderboardValue,
    props.sortingLeaderboardValue,
    props.setSortingLeaderboardValue,
    props.setFilterLeaderboardValue,
  ]);

  const handleWasteReducedClick = useCallback(() => {
    if (props.filterLeaderboardValue === 3) {
        props.setSortingLeaderboardValue(
            props.sortingLeaderboardValue === SortOrder.Descending
            ? SortOrder.Ascending
            : SortOrder.Descending
        );
    } else {
        props.setFilterLeaderboardValue(3);
        props.setSortingLeaderboardValue(SortOrder.Descending);
    }
  }, [
    props.filterLeaderboardValue,
    props.sortingLeaderboardValue,
    props.setSortingLeaderboardValue,
    props.setFilterLeaderboardValue,
  ]);
  return (
    <div className="row">
      <Container
        title={
          props.isTippingUser ? "Levels" : "Waste Recovered and Recycled, t"
        }
        blockStyle={props.isTippingUser && props.levelDataTable.length > 0}
        darkTheme={props.darkMode}
      >
        {props.isTippingUser ? (
          <>
            {props.levelDataTable.length > 0 ? (
              <div
                className={
                  "pie-graph-container" + (props.darkMode ? " dark" : "")
                }
              >
                <ResponsiveContainer height={250}>
                  <PieChart style={{ cursor: "pointer" }}>
                    <Pie
                      data={props.levelDataTable}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius="80%"
                      innerRadius="40%"
                    />
                    <Legend
                      verticalAlign="top"
                      wrapperStyle={{ width: "100%" }}
                      content={<LevelLegend />}
                    />
                    <Tooltip
                      cursor={false}
                      wrapperClassName={
                        "tooltip-wrapper radial-chart" +
                        (props.darkMode ? " dark" : "")
                      }
                      content={<WTPTooltip />}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="no-data-container">
                <p className="no-data-text">Awaiting data to be uploaded</p>
              </div>
            )}
          </>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              height={250}
              data={props.overallRecycledWastes}
              style={{ cursor: "pointer" }}
              margin={{ left: -30 }}
            >
              <XAxis
                dataKey="name"
                tick={{
                  fill: props.darkMode ? "#fff" : "#666",
                  fontSize: "var(--paragraph-font)",
                }}
              />
              <YAxis
                tick={{
                  fill: props.darkMode ? "#fff" : "#666",
                  fontSize: "var(--paragraph-font)",
                }}
                minTickGap={10}
              />
              <Tooltip
                cursor={false}
                wrapperClassName={
                  "tooltip-wrapper recycled-chart" +
                  (props.darkMode ? " dark" : "")
                }
              />
              <Legend content={<RecycledRecoveredLegend />} />
              {props.overallRecycledWastesColors.map((item, idx) => (
                <Bar
                  key={"waste-type-colors-home-" + idx}
                  dataKey={item.dataKey}
                  stackId="a"
                  fill={item.color}
                  stroke="#fff"
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Container>
      <Container
        title={props.isTippingUser ? "Tonnage" : "CO2 Emissions, t"}
        darkTheme={props.darkMode}
      >
        {props.isTippingUser ? (
          <>
            {props.tonnageDataTable.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  height={250}
                  data={props.tonnageDataTable}
                  style={{ cursor: "pointer" }}
                  margin={{ left: -30 }}
                >
                  <XAxis
                    dataKey="name"
                    tick={{
                      fill: props.darkMode ? "#fff" : "#666",
                      fontSize: "var(--paragraph-font)",
                    }}
                  />
                  <YAxis
                    tick={{
                      fill: props.darkMode ? "#fff" : "#666",
                      fontSize: "var(--paragraph-font)",
                    }}
                    minTickGap={10}
                  />
                  <Tooltip
                    cursor={false}
                    wrapperClassName={
                      "tooltip-wrapper recycled-chart" +
                      (props.darkMode ? " dark" : "")
                    }
                    formatter={(value: any) => [
                      `Total: ${Number(value).toLocaleString("en-GB")}t`,
                    ]}
                  />
                  <Bar dataKey="value" fill="#1f77b4" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data-container">
                <p className="no-data-text">Awaiting data to be uploaded</p>
              </div>
            )}
          </>
        ) : (
          <>
            {props.emissionsGeneratedDetails.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  width={250}
                  height={250}
                  data={props.emissionsGeneratedDetails}
                  margin={{ left: -30, right: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 0"
                    vertical={false}
                    stroke={props.darkMode ? "#fff" : "#666"}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{
                      fill: props.darkMode ? "#fff" : "#666",
                      fontSize: "var(--paragraph-font)",
                    }}
                    minTickGap={10}
                  />
                  <YAxis
                    tick={{
                      fill: props.darkMode ? "#fff" : "#666",
                      fontSize: "var(--paragraph-font)",
                    }}
                  />
                  <Tooltip
                    wrapperClassName={
                      "tooltip-wrapper" + (props.darkMode ? " dark" : "")
                    }
                    formatter={(value: any) => [
                      `Total: ${Number(value).toLocaleString("en-GB")}t`,
                    ]}
                  />
                  <Legend />
                  <Line
                    name="emissions"
                    type="monotone"
                    dataKey="value"
                    stroke={props.darkMode ? "#00cccc" : "#00b2b2"}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data-container">
                <p className="no-data-text">Awaiting data to be uploaded</p>
              </div>
            )}
          </>
        )}
      </Container>
      <Container
        title={props.isTippingUser ? "Spend" : "Leaderboard"}
        darkTheme={props.darkMode}
      >
        {props.isTippingUser ? (
          <>
            {props.feeDataTable.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  data={props.feeDataTable}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    tick={{
                      fill: props.darkMode ? "#fff" : "#666",
                      fontSize: "var(--paragraph-font)",
                    }}
                  />
                  <YAxis
                    tick={{
                      fill: props.darkMode ? "#fff" : "#666",
                      fontSize: "var(--paragraph-font)",
                    }}
                  />
                  <Tooltip
                    cursor={false}
                    wrapperClassName={
                      "tooltip-wrapper recycled-chart" +
                      (props.darkMode ? " dark" : "")
                    }
                    formatter={(name: any, value: any) => [
                      `${value}: £${Number(name).toLocaleString("en-GB")}`,
                    ]}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="fee"
                    name="Fee"
                    fill="#1f77b4"
                    stroke="#1f77b4"
                  />
                  <Line
                    type="monotone"
                    dataKey="rebate"
                    name="Rebate"
                    fill="#ff7f0e"
                    stroke="#ff7f0e"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data-container">
                <p className="no-data-text">Awaiting data to be uploaded</p>
              </div>
            )}
          </>
        ) : (
          <div className="filterable-leaderboard">
            <div className="graph-filter-row">
              <div
                className="filter-button"
                style={{
                  color: "var(--text-light-hc-color)",
                  backgroundColor: props.darkMode ? "#00cccc" : "#00b2b2",
                  opacity:
                    props.filterLeaderboardValue === 0 ||
                    props.filterLeaderboardValue === 1
                      ? 1
                      : 0.5,
                }}
                onClick={handleRecyclingRateClick}
              >
                {"% Recycling Rate " +
                  (props.filterLeaderboardValue === 1 &&
                  props.sortingLeaderboardValue === SortOrder.Ascending
                    ? "\u25b2"
                    : "\u25bc")}
              </div>
              <div
                className="filter-button"
                style={{
                  color:
                    "var(--text-" +
                    (props.darkMode ? "light-hc" : "dark") +
                    "-color)",
                  backgroundColor: props.darkMode ? "#7c97de" : "#122048",
                  opacity:
                    props.filterLeaderboardValue === 0 ||
                    props.filterLeaderboardValue === 2
                      ? 1
                      : 0.5,
                }}
                onClick={handleEmissionsRateClick}
              >
                {"% Emissions Rate " +
                  (props.filterLeaderboardValue === 2 &&
                  props.sortingLeaderboardValue === SortOrder.Descending
                    ? "\u25bc"
                    : "\u25b2")}
              </div>
              <div
                className="filter-button"
                style={{
                  color: "var(--text-light-hc-color)",
                  backgroundColor: "#ff3600",
                  opacity:
                    props.filterLeaderboardValue === 0 ||
                    props.filterLeaderboardValue === 3
                      ? 1
                      : 0.5,
                }}
                onClick={handleWasteReducedClick}
              >
                {"% Waste Reduced " +
                  (props.filterLeaderboardValue === 3 &&
                  props.sortingLeaderboardValue === SortOrder.Ascending
                    ? "\u25b2"
                    : "\u25bc")}
              </div>
            </div>
            {props.filterLeaderboardValue === 1 && (
              <div
                className={
                  "filter-description" + (props.darkMode ? " dark" : "")
                }
              >
                {`This showcases the percentage of a facility's total waste that is recycled for
                  selected period. In ${
                    props.sortingLeaderboardValue === SortOrder.Descending
                      ? "descending"
                      : "ascending"
                  }
                  order.`}
              </div>
            )}
            {props.filterLeaderboardValue === 2 && (
              <div
                className={
                  "filter-description" + (props.darkMode ? " dark" : "")
                }
              >
                {`This chart displays a facility's total emissions divided by total waste for
                  selected period, ranked from ${
                    props.sortingLeaderboardValue === SortOrder.Ascending
                      ? "lower"
                      : "higher"
                  }-impact
                  to ${
                    props.sortingLeaderboardValue === SortOrder.Ascending
                      ? "higher"
                      : "lower"
                  }-impact.`}
              </div>
            )}
            {props.filterLeaderboardValue === 3 && (
              <div
                className={
                  "filter-description" + (props.darkMode ? " dark" : "")
                }
              >
                {`This chart ranks facilities based on their percentage of waste reduction (current
                  YTD vs previous YTD), highlighting those who are ${
                    props.sortingLeaderboardValue === SortOrder.Descending
                      ? ""
                      : "not"
                  }
                  leading the way in sustainable practices.`}
              </div>
            )}
            <LeaderboardGraph
              arrayData={
                props.filterLeaderboardValue === 1 
                  ? (props.sortingLeaderboardValue === SortOrder.Descending 
                      ? props.leaderboardRecycling.top 
                      : props.leaderboardRecycling.bottom)
                  : props.filterLeaderboardValue === 2 
                    ? (props.sortingLeaderboardValue === SortOrder.Ascending 
                        ? props.leaderboardEmissions.top 
                        : props.leaderboardEmissions.bottom)
                    : (props.sortingLeaderboardValue === SortOrder.Descending 
                        ? props.leaderboardWaste.top 
                        : props.leaderboardWaste.bottom)
              }
              darkMode={props.darkMode}
              filterVal={props.filterLeaderboardValue}
              sortOrder={props.sortingLeaderboardValue}
              width={props.width}
            />
          </div>
        )}
      </Container>
    </div>
  );
});

export default EnvironmentRow;
